import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderAPI } from '../services/api';
import AuthModal from '../components/AuthModal';
import './OrderSuccess.css';
import { useDocumentMeta } from '../seo/useDocumentMeta';

const OrderSuccess = () => {
  useDocumentMeta();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setAuthReturnPath } = useAuth();
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    // 从URL参数或localStorage获取订单号
    const orderNo = searchParams.get('orderNo') || localStorage.getItem('lastOrderNumber');
    if (orderNo) {
      setOrderNumber(orderNo);
      fetchOrderDetails(orderNo);
    } else {
      setError('No order number found');
      setLoading(false);
    }
  }, [searchParams]);

  const fetchOrderDetails = async (orderNo) => {
    try {
      setLoading(true);
      const result = await orderAPI.getOrder(orderNo);
      
      if (result.success) {
        setOrder(result.data);
      } else {
        setError(result.message || 'Failed to fetch order details');
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = () => {
    // 只设返回路径 + 开本页的弹窗；调 openAuthModal 会把 Header 那个也打开，两个叠在一起
    setAuthReturnPath('/');
    setShowAuthModal(true);
  };

  const handleViewPurchaseHistory = () => {
    navigate('/profile', { state: { activeTab: 'purchases' } });
  };

  if (loading) {
    return (
      <div className="order-success-page">
        <div className="order-success-container">
          <div className="loading-container">
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-success-page">
        <div className="order-success-container">
          <h1 className="success-title">ORDER CONFIRMED</h1>
          <div className="success-message">
            <p>Your order #{orderNumber} has been successfully placed.</p>
            {error && <p className="error-text">{error}</p>}
          </div>
          <div className="success-actions">
            <Link to="/" className="action-btn continue-shopping-btn">
              CONTINUE SHOPPING
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatAddress = (address) => {
    const parts = [
      address.address,
      address.apartment,
      `${address.city}, ${address.state} ${address.postalCode}`,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const estimatedDelivery = new Date(date);
    estimatedDelivery.setDate(estimatedDelivery.getDate() + 3);
    return estimatedDelivery.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="order-success-page">
      <div className="order-success-container">
        <h1 className="success-title">THANK YOU FOR YOUR ORDER</h1>
        
        <div className="success-message">
          <p>
            Your order #{order.orderNumber} has been successfully placed.
            {isAuthenticated 
              ? ' You can track your order in your account.' 
              : ' A confirmation email has been sent to your email address.'}
          </p>
        </div>

        <div className="success-actions">
          {isAuthenticated ? (
            <button 
              onClick={handleViewPurchaseHistory}
              className="action-btn view-orders-btn"
            >
              VIEW PURCHASE HISTORY
            </button>
          ) : (
            <button 
              onClick={handleCreateAccount}
              className="action-btn create-account-btn"
            >
              CREATE AN ACCOUNT
            </button>
          )}
          <Link to="/" className="action-btn continue-shopping-btn">
            CONTINUE SHOPPING
          </Link>
        </div>

        <div className="order-details">
          <div className="detail-section">
            <h3>EMAIL ADDRESS</h3>
            <p>{order.email}</p>
          </div>

          <div className="detail-section">
            <h3>SHIPPING</h3>
            <p>
              {order.shippingAddress.firstName} {order.shippingAddress.lastName}
            </p>
            <p>{formatAddress(order.shippingAddress)}</p>
            <p>{order.shippingAddress.phone}</p>
            <p>by Standard Shipping</p>
            {order.shipping?.estimatedDelivery && (
              <p>Arrives by {formatDate(order.shipping.estimatedDelivery)}</p>
            )}
          </div>

          <div className="detail-section">
            <h3>PAYMENT</h3>
            <p>
              Paying with credit card{order.payment?.cardLastFour ? ` ending with ${order.payment.cardLastFour}` : ''}
            </p>
            {order.billingAddress && (
              <>
                <p>Billing to {order.billingAddress.firstName} {order.billingAddress.lastName}</p>
                <p>{formatAddress(order.billingAddress)}</p>
                {order.billingAddress.phone && <p>{order.billingAddress.phone}</p>}
              </>
            )}
          </div>

          <div className="detail-section">
            <h3>ORDER SUMMARY</h3>
            <div className="order-items">
              {order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <img 
                    src={(item.images && item.images.length > 0 ? item.images[0] : item.image) || '/placeholder.jpg'} 
                    alt={item.name}
                    className="item-image"
                  />
                  <div className="item-info">
                    <h4>{item.name}</h4>
                    <p>${item.price.toFixed(2)}</p>
                    <p>Qty: {item.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal</span>
                <span>${order.pricing?.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="total-row">
                <span>Shipping</span>
                <span>
                  {order.pricing?.shipping === 0 ? 'FREE' : `$${order.pricing?.shipping?.toFixed(2) || '0.00'}`}
                </span>
              </div>
              <div className="total-row">
                <span>Sales Tax</span>
                <span>${order.pricing?.tax?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="total-row total-final">
                <span>Total</span>
                <span>${order.pricing?.total?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default OrderSuccess;
